import React from "react";
import { CircleAlert, Loader, Radio, WifiOff } from "lucide-react";
import { DEMO_DATA_LABEL } from "./stubData";

/**
 * EngineModeChip — the one place on the tab that says whether the numbers
 * around it are the engine's or a local model (SUPERBETA-7).
 *
 * COMPLIANCE: live values are labelled live and demo values are labelled demo,
 * and the two are never interchangeable. This chip is the whole difference on
 * screen between "your account" and "an illustration", so it is persistent —
 * it does not auto-dismiss, it is not a toast, and demo mode never renders
 * without it.
 *
 * Four states, because collapsing any two of them would state something untrue:
 *   connecting — configured, first read in flight. Claims nothing either way.
 *   demo       — no engine, or no answer yet. Everything around it is modelled.
 *   live       — the engine answered. These are the member's own figures.
 *   live+stale — the engine answered, then stopped. Still the member's own
 *                figures, just old. Downgrading this to "demo" would relabel
 *                real money as an illustration; leaving it as a plain "live"
 *                would present a frozen number as a current one.
 *
 * No performance claim, no dollar figure, no promise — this chip states a
 * connection fact and nothing else.
 */

/* "just now" / "3m ago" — coarse on purpose. A ticking seconds counter implies
   a precision the poll interval does not have. */
export const freshnessLabel = (lastUpdatedMs, nowMs) => {
  if (!lastUpdatedMs) return null;
  const secs = Math.max(0, Math.round(((nowMs ?? Date.now()) - lastUpdatedMs) / 1000));
  if (secs < 60) return "updated just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `updated ${mins}m ago`;
  return `updated ${Math.round(mins / 60)}h ago`;
};

const EngineModeChip = ({ mode, stale = false, lastUpdatedMs = null, className = "" }) => {
  const freshness = freshnessLabel(lastUpdatedMs);

  let icon;
  let text;
  let title;
  let tone;

  if (mode === "connecting") {
    icon = <Loader size={12} aria-hidden="true" />;
    text = "Connecting to the engine…";
    title = "Reading your account from the signal engine.";
    tone = "sa-st-idle";
  } else if (mode === "live" && stale) {
    icon = <CircleAlert size={12} aria-hidden="true" />;
    text = freshness ? `Live values · reconnecting — ${freshness}` : "Live values · reconnecting";
    title =
      "These are your engine values, but the last refresh didn't get through. " +
      "Retrying on a backoff — nothing has changed on your account.";
    tone = "sa-st-recon";
  } else if (mode === "live") {
    icon = <Radio size={12} aria-hidden="true" />;
    text = freshness ? `Live — engine connected · ${freshness}` : "Live — engine connected";
    title = "Values on this tab are read from the signal engine.";
    tone = "sa-st-auto";
  } else {
    icon = <WifiOff size={12} aria-hidden="true" />;
    text = DEMO_DATA_LABEL;
    title =
      "The signal engine isn't connected in this environment, so the figures on " +
      "this tab come from a local model. They are not your account.";
    tone = "sa-st-idle";
  }

  return (
    <span
      className={["sa-st", tone, "sa-engine-chip", className].join(" ").trim()}
      data-testid="sa-engine-chip"
      data-mode={mode}
      data-stale={stale ? "true" : "false"}
      role="status"
      title={title}
    >
      {icon}
      {text}
    </span>
  );
};

export default EngineModeChip;
